import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { setData } from '../../features/mainSlice';
import { color } from '../../app/theme';
import TextField from '../shared/TextField';
import { PlatformType } from '../../app/types';
import { isValidMessageLimit } from '../../app/validators';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 12,
    },
    block: {
      display: 'flex',
      alignItems: 'center',
    },
    option: {
      marginRight: 36,
    },
    radioInput: {
      fontSize: 15,
    },
    radio: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: 160,
      margin: '0 8px',
    },
    errorText: {
      fontSize: 13,
      lineHeight: '18px',
      color: color.$17,
      marginTop: 8,
    },
  },
  {
    name: 'MessageNum',
  },
);

const MessageNum = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [radioType, setRadioType] = useState('off');
  const { limits, platform } = useSelector(state => state.ma?.data);
  const { message } = limits || {};

  const onChange = value => {
    if (value === 'on') {
      dispatch(
        setData({
          limits: {
            ...limits,
            message: 1,
          },
        }),
      );
    } else {
      dispatch(
        setData({
          limits: {
            ...limits,
            message: undefined,
          },
        }),
      );
    }
  };

  // Keep what the user typed: a valid digits-only value is stored as a number, anything else
  // ('', '0', 'abc', '1.5') stays as the raw string so the save gate can see and block it.
  const setNum = event => {
    const raw = event?.target?.value ?? '';
    dispatch(
      setData({
        limits: {
          ...limits,
          message: isValidMessageLimit(raw) ? parseInt(raw, 10) : raw,
        },
      }),
    );
  };

  useEffect(() => {
    setRadioType(message === undefined ? 'off' : 'on');
  }, [message]);

  if (platform !== PlatformType.LINE) return null;

  const isOn = message !== undefined;
  const isMessageError = isOn && !isValidMessageLimit(message);

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('setting.messageNum')}</div>
      <div className={classes.subtitle}>{t('setting.messageNum-subtitle')}</div>
      <FormControl component="fieldset">
        <RadioGroup
          row
          aria-label="default-display"
          name="default-display"
          value={radioType}
          onChange={e => onChange(e?.target?.value)}
        >
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value="on"
            control={<Radio color="primary" />}
            label={
              <div className={classes.radio}>
                {t('setting.messageNum-on')}
                <TextField
                  className={classes.input}
                  type="text"
                  isValid
                  placeholder="1"
                  value={isOn && message !== null ? String(message) : ''}
                  onChange={setNum}
                  disabled={!isOn}
                  error={isMessageError}
                  isError={isMessageError}
                />
                {t('setting.messageNum-on-2')}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value="off"
            control={<Radio color="primary" />}
            label={
              <div className={classes.radio}>{t('setting.messageNum-off')}</div>
            }
          />
        </RadioGroup>
      </FormControl>
      {isMessageError && (
        <div className={classes.errorText}>
          {t('error-invalid-limits-message')}
        </div>
      )}
    </div>
  );
};

export default MessageNum;
